export interface SearchHighlightSegment {
  text: string;
  matched: boolean;
}

interface MatchRange {
  start: number;
  end: number;
}

// Keep query normalization aligned with ItemContainerSearchQueryNormalizer.
function queryTokens(query: string): string[] {
  return [...new Set(query.normalize('NFKC').toLowerCase().match(/[\p{L}\p{Nd}]+/gu) ?? [])];
}

function findMatches(text: string, query: string): MatchRange[] {
  const folded = text.toLowerCase();
  const offsets = originalOffsets(text);
  const ranges: MatchRange[] = [];
  for (const token of queryTokens(query)) {
    let start = folded.indexOf(token);
    while (start >= 0) {
      ranges.push({start: offsets[start].start, end: offsets[start + token.length - 1].end});
      start = folded.indexOf(token, start + 1);
    }
  }
  ranges.sort((a, b) => a.start - b.start || b.end - a.end);
  return mergeMatches(ranges);
}

function originalOffsets(text: string): MatchRange[] {
  const offsets: MatchRange[] = [];
  let offset = 0;
  for (const character of text) {
    offsets.push(...Array<MatchRange>(character.toLowerCase().length).fill({start: offset, end: offset + character.length}));
    offset += character.length;
  }
  return offsets;
}

function mergeMatches(ranges: MatchRange[]): MatchRange[] {
  const merged: MatchRange[] = [];
  for (const range of ranges) {
    const previous = merged[merged.length - 1];
    if (previous && range.start <= previous.end) {
      previous.end = Math.max(previous.end, range.end);
    } else {
      merged.push({...range});
    }
  }
  return merged;
}

export function highlightSearchText(text: string, query: string, context = false): SearchHighlightSegment[] {
  if (!text || !query.trim()) {
    return [{text, matched: false}];
  }
  const matches = findMatches(text, query);
  if (!matches.length) {
    return [{text, matched: false}];
  }

  const first = matches[0];
  const start = context ? Math.max(0, first.start - 12) : 0;
  const end = context ? Math.min(text.length, Math.max(start + 160, first.end)) : text.length;
  const segments: SearchHighlightSegment[] = [];
  if (start > 0) {
    segments.push({text: '…', matched: false});
  }
  let cursor = start;
  for (const match of matches.filter(range => range.start < end)) {
    if (match.start > cursor) {
      segments.push({text: text.slice(cursor, match.start), matched: false});
    }
    segments.push({text: text.slice(match.start, Math.min(match.end, end)), matched: true});
    cursor = Math.min(match.end, end);
  }
  if (cursor < end) {
    segments.push({text: text.slice(cursor, end), matched: false});
  }
  if (end < text.length) {
    segments.push({text: '…', matched: false});
  }
  return segments;
}
